import { PartnerSkills } from "Infrastructure/Api/Api";
import { Resources } from "Infrastructure/Translation/Resources";
import { getPartnerAddressInfoSchema } from "Partner/Components/Form/Schema/partnerAddressInfoSchema";
import { getPartnerBankAccountInfoSchema } from "Partner/Components/Form/Schema/partnerBankAccountInfoSchema";
import { partnerBillingAddressSchema } from "Partner/Components/Form/Schema/partnerBillingAddressSchema";
import { getPartnerBillingProfileSchema } from "Partner/Components/Form/Schema/partnerBillingProfileSchema";
import type { TOptions } from "i18next";
import type { ObjPathProxy } from "ts-object-path";
import { z } from "zod";

const partnerResource =
	Resources.base.partner.validationMessages.partnerGeneral;

const CreatePartnerFormModelSchema = z.object({});

export const getCreatePartnerFormModelSchema = (
	t: (
		resourcePath: ObjPathProxy<unknown, string>,
		options?: TOptions,
	) => string,
) =>
	CreatePartnerFormModelSchema.extend({
		isSameAddress: z.boolean().default(true),
		partner: z.object({
			name: z.string().nonempty({ message: `${t(partnerResource.name)}` }),
			middleName: z.string().nullish(),
			surname: z
				.string()
				.nonempty({ message: `${t(partnerResource.surname)}` }),
			phone: z.string().nonempty({ message: `${t(partnerResource.phone)}` }),
			email: z
				.string()
				.email({ message: `${t(partnerResource.emailFormat)}` })
				.nonempty({ message: `${t(partnerResource.email)}` }),
			teamSize: z
				.number()
				.int()
				.min(1, { message: `${t(partnerResource.teamSize)}` })
				.optional()
				.nullable(),
			isCanTrainDay: z.boolean().nullish(),
			marketId: z
				.number({ message: `${t(partnerResource.marketId)}` })
				.int({ message: `${t(partnerResource.marketId)}` }),
			marketRegionId: z
				.number()
				.int()
				.refine(
					(value) => {
						return !!value;
					},
					{
						message: `${t(partnerResource.marketRegionId)}`,
					},
				),
			verticals: z
				.array(z.coerce.number())
				.min(1, { message: `${t(partnerResource.verticals)}` }),
			skills: z.array(z.nativeEnum(PartnerSkills)).optional(),
			notes: z.string().nullish(),
		}),
	})
		.extend({
			address: getPartnerAddressInfoSchema(),
		})
		.extend({
			billingProfile: getPartnerBillingProfileSchema(),
		})
		.extend({
			bankAccount: getPartnerBankAccountInfoSchema(),
		})
		.extend({
			billingAddress: partnerBillingAddressSchema().nullish(),
		});

export type PartnerFormModel = z.infer<
	ReturnType<typeof getCreatePartnerFormModelSchema>
>;
